import type { GalleryFixture } from "./catalog";
import type { GridQuestionGroupRaw } from "../questions/gridQuestionGroup/core";
import gridQuestionGroup from "./fixtures/grid-question-group.json";

const options = [
  {
    value: 30,
    text: "Excellent",
    disabled: false,
    unique: false,
    display_text: true,
  },
  {
    value: 10,
    text: "Good",
    disabled: false,
    unique: false,
    display_text: true,
  },
  {
    value: 70,
    text: "Poor",
    disabled: false,
    unique: false,
    display_text: true,
  },
  {
    value: 50,
    text: "Terrible",
    disabled: false,
    unique: false,
    display_text: true,
  },
];
const option_groups = [
  { text: "Positive", values: [30, 10] },
  { text: "Negative", values: [70, 50] },
];
const rows = [
  { value: "noise", text: "Noise", display_text: true },
  { value: "lighting", text: "Lighting", display_text: true },
  { value: "kitchen", text: "Kitchen", display_text: true },
  { value: "break-room", text: "Break room", display_text: true },
];
const row_groups = [
  { text: "Work Environment", values: ["noise", "lighting"] },
  { text: "Social Spaces", values: ["kitchen", "break-room"] },
];
const dont_know_option = { value: "unknown", text: "Don't know" };
const pairRows = [{ value: "row2" }, { value: "row1" }, { value: "row3" }];
const pairGroups = [
  { text: "Work Environment", values: ["row2", "row1"] },
  { text: "Social Spaces", values: ["row3"] },
];
const pairs = [
  ["Quiet", "Lively"],
  ["Bright", "Dim"],
  ["Small kitchen", "Large kitchen"],
];

export const visualGroupFixtures: readonly GalleryFixture[] = [
  {
    id: "select-grouped",
    type: "select",
    description: "Choices under non-selectable headings",
    request: {
      type: "select",
      content: "How was your visit?",
      options,
      option_groups,
    },
    previousAnswer: 70,
    expected: {
      response: { answerJson: { option: 30 }, type: "select", value: 30 },
    },
    features: ["visual-groups"],
  },
  {
    id: "multi-select-grouped",
    type: "multi-select",
    description: "One selection limit across choice groups",
    request: {
      type: "multi-select",
      content: "Choose up to two descriptions.",
      options,
      option_groups,
      max_options: 2,
    },
    previousAnswer: [30, 70],
    expected: {
      response: {
        answerJson: [{ option: 30 }, { option: 70 }],
        type: "multi-select",
        value: "30,70",
        responses: "30\t70",
      },
    },
    features: ["visual-groups"],
  },
  {
    id: "rank-sort-grouped",
    type: "rank_sort",
    description: "Grouped choices with one overall ranking",
    request: {
      type: "rank_sort",
      content: "Rank the descriptions.",
      labels: ["Least", "Most"],
      options,
      option_groups,
      min_options: 1,
      max_options: 3,
      dont_know_option,
    },
    previousAnswer: ["70", "30"],
    expected: {
      response: { type: "rank_sort", value: "70,30", responses: "70\t30" },
    },
    features: ["visual-groups"],
  },
  {
    id: "grid-select-grouped",
    type: "grid_select",
    description: "Grouped rows and choice columns",
    request: {
      type: "grid_select",
      content: "How would you rate these spaces?",
      rows,
      row_groups,
      options,
      option_groups,
      dont_know_option,
    },
    previousAnswer: [30, 10, 70, 50],
    expected: {
      response: {
        answerJson: [
          { option: 30 },
          { option: 10 },
          { option: 70 },
          { option: 50 },
        ],
        type: "grid_select",
        value: "30,10,70,50",
        responses: "30\t10\t70\t50",
      },
    },
    features: ["visual-groups"],
  },
  {
    id: "grid-multi-select-grouped",
    type: "grid_multi_select",
    description: "Grouped rows and checkbox columns",
    request: {
      type: "grid_multi_select",
      content: "Choose descriptions for each space.",
      rows,
      row_groups,
      options,
      option_groups,
      dont_know_option,
    },
    previousAnswer: [[30], [10], [70], [50]],
    expected: {
      response: {
        answerJson: [
          [{ option: 30 }],
          [{ option: 10 }],
          [{ option: 70 }],
          [{ option: 50 }],
        ],
        type: "grid_multi_select",
        value: "30|10|70|50",
        responses: "30\t10\t70\t50",
      },
    },
    features: ["visual-groups"],
  },
  {
    id: "grid-rating-grouped",
    type: "grid_rating",
    description: "Grouped rows on a continuous rating scale",
    request: {
      type: "grid_rating",
      content: "Rate each space.",
      rows,
      row_groups,
      options: [
        { value: 0, text: "Poor", display_text: true },
        { value: 1, text: "Fair", display_text: true },
        { value: 2, text: "Good", display_text: true },
      ],
      dont_know_option,
    },
    previousAnswer: [0, 1, 2, 0],
    expected: {
      response: {
        type: "grid_rating",
        value: "0,1,2,0",
        responses: "0\t1\t2\t0",
      },
    },
    features: ["visual-groups"],
  },
  {
    id: "grid-numeric-grouped",
    type: "grid_numeric",
    description: "Grouped rows and numeric columns with totals",
    request: {
      type: "grid_numeric",
      content: "How many hours did you spend in each space?",
      rows,
      row_groups,
      columns: ["Monday", "Tuesday", "Saturday", "Sunday"],
      column_groups: [
        { text: "Weekdays", values: ["Monday", "Tuesday"] },
        { text: "Weekend", values: ["Saturday", "Sunday"] },
      ],
      autosum_rows: true,
      autosum_columns: false,
      dont_know_option,
    },
    previousAnswer: ["1,2,3,4", "5,6,7,8", "0,1,0,1", "2,0,2,0"],
    expected: {
      response: {
        type: "grid_numeric",
        value: "1,2,3,4,5,6,7,8,0,1,0,1,2,0,2,0",
        responses: "1,2,3,4\t5,6,7,8\t0,1,0,1\t2,0,2,0",
      },
    },
    features: ["visual-groups"],
  },
  {
    id: "grid-this-or-that-grouped",
    type: "grid_this_or_that",
    description: "Grouped pairs using canonical row IDs after shuffling",
    request: {
      type: "grid_this_or_that",
      content: "Which would you prefer?",
      rows: pairRows,
      row_groups: pairGroups,
      row_options: pairs.map((pair) =>
        pair.map((text) => ({
          value: text,
          text,
          disabled: false,
          unique: false,
          display_text: true,
        })),
      ),
      dont_know_option,
    },
    previousAnswer: ["Quiet", "Dim", "Small kitchen"],
    expected: {
      response: {
        type: "grid_this_or_that",
        value: "Quiet,Dim,Small kitchen",
        responses: "Quiet\tDim\tSmall kitchen",
      },
    },
    features: ["visual-groups"],
  },
  {
    id: "grid-this-or-that-rating-grouped",
    type: "grid_this_or_that_rating",
    description: "Grouped pairwise scales after shuffling",
    request: {
      type: "grid_this_or_that_rating",
      content: "Rate your preference.",
      rows: pairRows,
      row_groups: pairGroups,
      row_options: pairs.map((pair) => [
        { value: 0, text: pair[0], display_text: true },
        { value: 1, text: "", display_text: false },
        { value: 2, text: pair[1], display_text: true },
      ]),
      dont_know_option,
    },
    previousAnswer: [0, 2, 1],
    expected: {
      response: {
        type: "grid_this_or_that_rating",
        value: "0,2,1",
        responses: "0\t2\t1",
      },
    },
    features: ["visual-groups"],
  },
  {
    id: "grid-question-group-grouped",
    type: "grid_question_group",
    description: "Group headings above repeated mixed question rows",
    request: {
      ...(gridQuestionGroup as GridQuestionGroupRaw),
      children: (gridQuestionGroup as GridQuestionGroupRaw).children.map(
        (child) => ({
          ...child,
          question: { ...child.question, error_message: undefined },
        }),
      ),
      row_groups: [
        { text: "School-age children", values: ["Child 1", "Child 2"] },
      ],
    },
    expected: { response: { type: "numeric", value: "8" } },
    features: ["visual-groups"],
  },
  {
    id: "numeric-grouped-context",
    type: "numeric",
    description: "Parent row and column headings on a split-grid child",
    request: {
      type: "numeric",
      content: "Hours in the kitchen on Monday?",
      row_groups: [{ text: "Social Spaces", values: ["kitchen"] }],
      column_groups: [{ text: "Weekdays", values: ["Monday"] }],
    },
    expected: { response: { type: "numeric", value: "3" } },
    features: ["visual-groups"],
  },
];
